import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';

// Import all your components
import DynamicMap from './DynamicMap';
import PhoneTracker from './phoneTracker';
import Register from './Register';
import Dashboard from './Dashboard';
import Home from './Home';
import Login from './login';
import PhoneDetails from './PhoneDetails';
import BluetoothDialer from './BluetoothDialer';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';

// Navigation component with conditional rendering
const Navigation = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const navigate = useNavigate();

  useEffect(() => {
    // Check login status on mount
    const checkLoginStatus = () => {
      const loggedIn = localStorage.getItem('isLoggedIn') === 'true' || 
                      sessionStorage.getItem('isLoggedIn') === 'true';
      console.log('Login status check:', loggedIn); // Debug log
      setIsLoggedIn(loggedIn);
      setIsLoading(false);
    };

    checkLoginStatus();

    // Listen for storage changes (in case user logs in/out in another tab)
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLogout = () => {
    // Clear all storage
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('loginTime');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('isLoggedIn');
    sessionStorage.removeItem('token');
    
    setIsLoggedIn(false);
    navigate('/login');
  };

  // Don't render navigation until we've checked login status
  if (isLoading) {
    return null; // Or return a loading spinner
  }

  return (
    <nav style={styles.nav}>
      <Link to="/" style={styles.link}>Home</Link>
      <Link to="/register" style={styles.link}>Register</Link>
      <Link to="/login" style={styles.link}>Login</Link>
      
      {/* Protected links - only shown when logged in */}
      {isLoggedIn && (
        <>
          <Link to="/map" style={styles.link}>Map</Link>
          <Link to="/tracker" style={styles.link}>Tracker</Link>
          <Link to="/blue-tooth" style={styles.link}>Bluetooth</Link>
          <Link to="/dashboard" style={styles.link}>Dashboard</Link>
          <Link to="/phone-details" style={styles.link}>Phone Details</Link>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </>
      )}
    </nav>
  );
};

// Main App component
const App = () => {
  return (
    <div>
      <Navigation />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Protected routes */}
        <Route
          path="/map"
          element={
            <PrivateRoute>
              <DynamicMap />
            </PrivateRoute>
          }
        />
        <Route
          path="/tracker"
          element={
            <PrivateRoute>
              <PhoneTracker />
            </PrivateRoute>
          }
        />
        <Route
          path="/blue-tooth"
          element={
            <PrivateRoute>
              <BluetoothDialer />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/phone-details"
          element={
            <PrivateRoute>
              <PhoneDetails />
            </PrivateRoute>
          }
        />
      </Routes>
    </div>
  );
};

const styles = {
  nav: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    backgroundColor: '#850182B2',
    padding: '10px 0',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  link: {
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 'bold',
    padding: '5px 10px',
    borderRadius: '5px',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  logoutButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    padding: '5px 15px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    transition: 'all 0.3s',
    ':hover': {
      backgroundColor: '#c82333',
      transform: 'translateY(-2px)',
    },
  },
};

export default App;